import {
  Avatar,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Table, Button } from "react-bootstrap";
import { HiUsers } from "react-icons/hi";
import { HiClipboardDocumentList } from "react-icons/hi2";
import { BsPersonCheckFill, BsPersonFillAdd, BsCalendar3, BsFileEarmarkBarGraph, BsGift, BsBarChartLine, BsCheck2, BsEye, BsEyeSlash, BsPersonFill, BsBriefcaseFill, BsLockFill, BsInfoCircle } from "react-icons/bs";
import { IoClose } from "react-icons/io5";
import dayjs from "dayjs";
import { countWorkingDaysBetween, buildHolidaySet, pktMidnightISO } from "../../../utils/leaveDays";
import emailjs from "emailjs-com";
import "./Dashboard.css";
import { useTheme } from "../../../context/ThemeContext";

const ICON_MAP = {
  people: HiUsers,
  "person-check": BsPersonCheckFill,
  calendar: HiClipboardDocumentList,
  "person-add": BsPersonFillAdd,
};

const Dashboard = (props) => {
  const { user, onViewAllEmployees } = props;
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isCommandCenter = props.commandCenter === true && (user?.role === "HR" || user?.role === "Executive");
  const filterColor = isDark ? "#D4AF37" : "#4484FF";
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [activeEmployeesCount, setActiveEmployeesCount] = useState(0);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [onLeaveByEmail, setOnLeaveByEmail] = useState({}); // email -> "manager" | "both"
  const [managerRoster, setManagerRoster] = useState([]); // manager's own team (Employee role)
  const [pendingHrLeavesCount, setPendingHrLeavesCount] = useState(0);
  const [newHiresCount, setNewHiresCount] = useState(0);
  const [newHiresBadge, setNewHiresBadge] = useState(null);
  const [newHiresList, setNewHiresList] = useState([]);
  const [showNewHiresPopup, setShowNewHiresPopup] = useState(false);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [leaveToast, setLeaveToast] = useState({ visible: false, message: "" });
  const [weeklyAttendance, setWeeklyAttendance] = useState([]);
  const [weeklyAvgPct, setWeeklyAvgPct] = useState(0);
  const [managerTeams, setManagerTeams] = useState([]);
  const [selectedManagerEmail, setSelectedManagerEmail] = useState("ALL");
  const [showAddEmpModal, setShowAddEmpModal] = useState(false);

  // Schedule Leave modal state
  const [showScheduleLeaveModal, setShowScheduleLeaveModal] = useState(false);
  const [leaveActiveType, setLeaveActiveType] = useState("Annual Leave");
  const [creditedAnnualLeave, setCreditedAnnualLeave] = useState(0);
  const [remainingAnnualLeave, setRemainingAnnualLeave] = useState(0);
  const [creditedPersonalDays, setCreditedPersonalDays] = useState(0);
  const [remainingPersonalDays, setRemainingPersonalDays] = useState(0);
  const [leaveFromDate, setLeaveFromDate] = useState("");
  const [leaveToDate, setLeaveToDate] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveError, setLeaveError] = useState("");
  const [leaveSuccess, setLeaveSuccess] = useState(false);
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);

  const fetchLeaveStats = async () => {
    try {
      const response = await fetch(`https://hr-backend-72v1.onrender.com/leaves?email=${user.email}`);
      if (response.ok) {
        const data = await response.json();
        if (data.leaveSummary) {
          setCreditedAnnualLeave(data.leaveSummary.annualLeavesCredited || 0);
          setRemainingAnnualLeave(data.leaveSummary.annualLeavesRemaining || 0);
          setCreditedPersonalDays(data.leaveSummary.personalDaysCredited || 0);
          setRemainingPersonalDays(data.leaveSummary.personalDaysRemaining || 0);
        }
      }
    } catch (err) {
      console.error("Error fetching leave stats:", err);
    }
  };

  const [holidaySet, setHolidaySet] = useState(() => new Set());
  const [holidaysLoaded, setHolidaysLoaded] = useState(false);

  // Load company holidays so leave day counts exclude weekends AND holidays.
  useEffect(() => {
    const loadHolidays = async () => {
      try {
        const res = await fetch("https://hr-backend-72v1.onrender.com/holidays");
        if (res.ok) {
          const data = await res.json();
          setHolidaySet(buildHolidaySet(Array.isArray(data) ? data : []));
          setHolidaysLoaded(true);
        }
      } catch (e) {
        /* best-effort: weekends still excluded even without holiday data */
      }
    };
    loadHolidays();
  }, []);

  const handleLeaveSubmit = async () => {
    if (!leaveFromDate || !leaveToDate) {
      setLeaveError("Both From Date and To Date are required.");
      return;
    }
    if (dayjs(leaveFromDate).isAfter(dayjs(leaveToDate))) {
      setLeaveError("From date must be earlier than To date.");
      return;
    }
    // Working days only: Mon-Fri, excluding company holidays.
    const requestedDays = countWorkingDaysBetween(
      dayjs(leaveFromDate).format("YYYY-MM-DD"),
      dayjs(leaveToDate).format("YYYY-MM-DD"),
      holidaySet
    );
    if (requestedDays === 0) {
      setLeaveError("Selected dates fall on weekends/holidays only - no working days to request.");
      return;
    }
    const remaining = leaveActiveType === "Annual Leave" ? remainingAnnualLeave : remainingPersonalDays;
    // Only enforce the limit once holidays have loaded (backend re-validates regardless).
    if (holidaysLoaded && requestedDays > remaining) {
      setLeaveError(`You can only request up to ${remaining} days for ${leaveActiveType}.`);
      return;
    }
    setLeaveError("");
    setLeaveSubmitting(true);
    try {
      const res = await fetch("https://hr-backend-72v1.onrender.com/applyleave", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user.email,
          name: user.name,
          // Store PKT-midnight so the count is correct regardless of the browser's timezone.
          fromDate: pktMidnightISO(leaveFromDate),
          toDate: pktMidnightISO(leaveToDate),
          reason: leaveReason,
          leaveType: leaveActiveType,
        }),
      });
      if (res.ok) {
        setLeaveSuccess(true);
        setLeaveFromDate("");
        setLeaveToDate("");
        setLeaveReason("");
        fetchLeaveStats();
      } else {
        const errData = await res.json();
        setLeaveError(errData.error || "Failed to submit leave request.");
      }
    } catch (err) {
      setLeaveError("An error occurred. Please try again.");
    } finally {
      setLeaveSubmitting(false);
    }
  };

  useEffect(() => {
    if (showScheduleLeaveModal) {
      fetchLeaveStats();
      setLeaveFromDate("");
      setLeaveToDate("");
      setLeaveReason("");
      setLeaveError("");
      setLeaveSuccess(false);
      setLeaveActiveType("Annual Leave");
    }
  }, [showScheduleLeaveModal]);

  // ── Add Employee stepper state ──
  const [addEmpStep, setAddEmpStep] = useState(0);
  const [addEmpName, setAddEmpName] = useState("");
  const [addEmpCnic, setAddEmpCnic] = useState("");
  const [addEmpPhone, setAddEmpPhone] = useState("");
  const [addEmpEmerPhone, setAddEmpEmerPhone] = useState("");
  const [addEmpPermAddr, setAddEmpPermAddr] = useState("");
  const [addEmpPresentAddr, setAddEmpPresentAddr] = useState("");
  const [addEmpDob, setAddEmpDob] = useState("");
  const [addEmpDept, setAddEmpDept] = useState("");
  const [addEmpManager, setAddEmpManager] = useState(null);
  const [addEmpPos, setAddEmpPos] = useState("");
  const [addEmpSalary, setAddEmpSalary] = useState("");
  const [addEmpJoining, setAddEmpJoining] = useState("");
  const [addEmpEmail, setAddEmpEmail] = useState("");
  const [addEmpPass, setAddEmpPass] = useState("");
  const [addEmpBankD, setAddEmpBankD] = useState("");
  const [addEmpShowPass, setAddEmpShowPass] = useState(false);
  const [addEmpErrors, setAddEmpErrors] = useState({});
  const [addEmpManagers, setAddEmpManagers] = useState([]);
  const [addEmpExistingEmails, setAddEmpExistingEmails] = useState([]);
  const [addEmpProcessing, setAddEmpProcessing] = useState(false);
  const [addEmpSuccess, setAddEmpSuccess] = useState(false);
  const [addEmpDupEmail, setAddEmpDupEmail] = useState(false);

  // Generate Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportModalTab, setReportModalTab] = useState(0);

  // Tab 1 - Progress Reports
  const [rptData, setRptData] = useState(null); // { submitted: [...], notSubmitted: [...] }
  const [rptLoading, setRptLoading] = useState(false);
  const [rptExpandedEmail, setRptExpandedEmail] = useState(null);

  // Tab 2 - Pending Items
  const [pendingSummary, setPendingSummary] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);

  // Tab 3 - Export
  const [expAttFrom, setExpAttFrom] = useState("");
  const [expAttTo, setExpAttTo] = useState("");
  const [expRptFrom, setExpRptFrom] = useState("");
  const [expRptTo, setExpRptTo] = useState("");
  const [expAttLoading, setExpAttLoading] = useState(false);
  const [expRptLoading, setExpRptLoading] = useState(false);

  const fetchTodayReports = async () => {
    setRptLoading(true);
    try {
      const nowPKT = new Date(Date.now() + 5 * 60 * 60 * 1000);
      const todayStr = nowPKT.toISOString().slice(0, 10);
      const [reportsRes, empRes] = await Promise.all([
        fetch(`https://hr-backend-72v1.onrender.com/reportshow?date=${todayStr}&limit=500`),
        fetch("https://hr-backend-72v1.onrender.com/employees/emails"),
      ]);
      const reportsData = await reportsRes.json();
      const empData = await empRes.json();
      const submittedEmails = new Set((reportsData.reports || []).map(r => r.email));
      const submitted = (reportsData.reports || []).map(r => ({ name: r.name, email: r.email, reportDetail: r.reportDetail }));
      const notSubmitted = (Array.isArray(empData) ? empData : []).filter(e => !submittedEmails.has(e.email));
      setRptData({ submitted, notSubmitted });
    } catch (e) {
      console.error("Error fetching today reports:", e);
    }
    setRptLoading(false);
  };

  const fetchPendingSummary = async () => {
    setPendingLoading(true);
    try {
      const res = await fetch("https://hr-backend-72v1.onrender.com/hr-pending-summary");
      const data = await res.json();
      setPendingSummary(data);
    } catch (e) {
      console.error("Error fetching pending summary:", e);
    }
    setPendingLoading(false);
  };

  useEffect(() => {
    if (showReportModal) {
      setReportModalTab(0);
      setRptData(null);
      setRptExpandedEmail(null);
      setPendingSummary(null);
      fetchTodayReports();
      fetchPendingSummary();
    }
  }, [showReportModal]);

  const downloadCSV = (rows, filename) => {
    if (!rows || rows.length === 0) return;
    const header = Object.keys(rows[0]).join(",");
    const body = rows.map(r =>
      Object.values(r).map(v => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAttendance = async () => {
    if (!expAttFrom || !expAttTo) return;
    setExpAttLoading(true);
    try {
      const res = await fetch(`https://hr-backend-72v1.onrender.com/export-attendance?from=${expAttFrom}&to=${expAttTo}`);
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-report-${expAttFrom}-to-${expAttTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setExpAttLoading(false);
  };

  const handleExportReports = async () => {
    if (!expRptFrom || !expRptTo) return;
    setExpRptLoading(true);
    try {
      const res = await fetch(`https://hr-backend-72v1.onrender.com/export-reports?from=${expRptFrom}&to=${expRptTo}`);
      const data = await res.json();
      downloadCSV(data.map(r => ({
        "Employee Name": r.name, "Email": r.email, "Date": r.date, "Report": r.report,
      })), `progress_reports_${expRptFrom}_to_${expRptTo}.csv`);
    } catch (e) { console.error(e); }
    setExpRptLoading(false);
  };

  // Birthdays modal state
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [birthdayData, setBirthdayData] = useState([]);
  const [birthdayLoading, setBirthdayLoading] = useState(false);

  const fetchBirthdays = async () => {
    setBirthdayLoading(true);
    try {
      const res = await fetch("https://hr-backend-72v1.onrender.com/birthdays");
      if (res.ok) setBirthdayData(await res.json());
    } catch (e) { console.error("Error fetching birthdays:", e); }
    setBirthdayLoading(false);
  };

  useEffect(() => {
    if (showBirthdayModal) fetchBirthdays();
  }, [showBirthdayModal]);

  // Lock background scroll when any modal is open
  useEffect(() => {
    const anyOpen = showAddEmpModal || showScheduleLeaveModal || showReportModal || showBirthdayModal;
    if (!anyOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showAddEmpModal, showScheduleLeaveModal, showReportModal, showBirthdayModal]);

  const getInitials = (name) => (name || "").trim()[0]?.toUpperCase() || "?";

  // Bar tooltip state
  const [hoveredBar, setHoveredBar] = useState(null); // date string "YYYY-MM-DD"
  const [tooltipCache, setTooltipCache] = useState({});
  const barHoverTimeout = useRef(null);

  const fetchAttendanceDetail = async (date) => {
    if (tooltipCache[date]) return;
    try {
      const res = await fetch(`https://hr-backend-72v1.onrender.com/attendance-detail?date=${date}`);
      if (res.ok) {
        const data = await res.json();
        setTooltipCache((prev) => ({ ...prev, [date]: data }));
      }
    } catch (e) {
      console.error("Error fetching attendance detail:", e);
    }
  };

  const handleBarHover = (date) => {
    clearTimeout(barHoverTimeout.current);
    setHoveredBar(date);
    fetchAttendanceDetail(date);
  };

  const handleBarLeave = () => {
    barHoverTimeout.current = setTimeout(() => setHoveredBar(null), 150);
  };

  const handleTooltipEnter = () => {
    clearTimeout(barHoverTimeout.current);
  };

  const addEmpDepartments = ["Marketing Team", "Sales Team", "Content Team", "Dev Team", "Media Buying", "Finance", "HR", "Executive"];

  useEffect(() => {
    if (showAddEmpModal) {
      setAddEmpStep(0);
      setAddEmpName(""); setAddEmpCnic(""); setAddEmpPhone(""); setAddEmpEmerPhone("");
      setAddEmpPermAddr(""); setAddEmpPresentAddr(""); setAddEmpDob("");
      setAddEmpDept(""); setAddEmpManager(null); setAddEmpPos(""); setAddEmpSalary(""); setAddEmpJoining("");
      setAddEmpEmail(""); setAddEmpPass(""); setAddEmpBankD("");
      setAddEmpErrors({}); setAddEmpProcessing(false); setAddEmpSuccess(false); setAddEmpDupEmail(false); setAddEmpShowPass(false);
      fetch("https://hr-backend-72v1.onrender.com/employees-with-manager").then(r => r.json()).then(d => setAddEmpManagers(Array.isArray(d) ? d : [])).catch(() => {});
      fetch("https://hr-backend-72v1.onrender.com/employees/emails").then(r => r.json()).then(d => setAddEmpExistingEmails(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [showAddEmpModal]);

  const validateAddEmpStep = (step) => {
    const errs = {};
    if (step === 0 && !addEmpName.trim()) errs.name = "Full Name is required";
    if (step === 2) {
      if (!addEmpEmail.trim()) errs.email = "Email is required";
      if (!addEmpPass.trim()) errs.pass = "Password is required";
    }
    setAddEmpErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddEmpNext = () => {
    if (validateAddEmpStep(addEmpStep)) setAddEmpStep(s => s + 1);
  };

  const handleAddEmpSubmit = async () => {
    if (!validateAddEmpStep(2)) return;
    if (addEmpExistingEmails.some(e => e.email === addEmpEmail)) {
      setAddEmpDupEmail(true);
      return;
    }
    setAddEmpProcessing(true);
    setAddEmpDupEmail(false);
    const role = addEmpDept === "Executive" ? "Executive" : "Employee";
    const joiningDate = addEmpJoining ? new Date(addEmpJoining) : new Date();
    const dobDate = addEmpDob ? new Date(addEmpDob) : new Date();
    try {
      const r1 = await fetch("https://hr-backend-72v1.onrender.com/employee", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: addEmpName, email: addEmpEmail, phone: addEmpPhone,
          emergencyPhone: addEmpEmerPhone, address: addEmpPermAddr,
          presentAddress: addEmpPresentAddr, bankD: addEmpBankD,
          joining: joiningDate, dob: dobDate, department: addEmpDept,
          leadManager: addEmpManager ? addEmpManager.name : "",
          designation: role, salary: addEmpSalary, pass: addEmpPass,
          pos: addEmpPos, cnic: addEmpCnic,
        }),
      });
      if (addEmpManager) {
        await fetch("https://hr-backend-72v1.onrender.com/assign-employee", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ managerEmail: addEmpManager.email, employeeName: addEmpName, employeeEmail: addEmpEmail }),
        });
      }
      if (r1.ok) {
        emailjs.send("service_3p7klz8", "template_97o71qy",
          { to_name: addEmpName, to_email: addEmpEmail, pass: addEmpPass, from_name: "HR", from_email: "hr@growthguild.us" },
          "e8b24L9zqgYrt6tz9"
        ).catch(() => {});
        setAddEmpSuccess(true);
      }
    } catch (err) {
      console.error("Error adding employee:", err);
    }
    setAddEmpProcessing(false);
  };

  const fetchTimers = async () => {
    try {
      const url =
        user.role === "Employee"
          ? `https://hr-backend-72v1.onrender.com/getActiveCheckIns?managerEmail=${user.email}`
          : "https://hr-backend-72v1.onrender.com/getActiveCheckIns";

      const response = await fetch(url);
      if (!response.ok)
        throw new Error("Failed to fetch Check Ins in Dashboard");
      const activeData = await response.json();

      setActiveEmployeesCount(activeData.length);
      setActiveEmployees(activeData);
    } catch (error) {
      console.error("Error fetching timer data:", error);
    }
  };

  const fetchOnLeave = async () => {
    try {
      const dateStr = new Date().toLocaleDateString("en-CA", {
        timeZone: "Asia/Karachi",
      }); // today's date (YYYY-MM-DD) in PKT
      const url =
        user.role === "Employee"
          ? `https://hr-backend-72v1.onrender.com/on-leave-today?date=${dateStr}&managerEmail=${user.email}`
          : `https://hr-backend-72v1.onrender.com/on-leave-today?date=${dateStr}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch on-leave");
      const data = await res.json();
      const map = {};
      (Array.isArray(data) ? data : []).forEach((r) => {
        map[r.email] = r.approval;
      });
      setOnLeaveByEmail(map);
    } catch (error) {
      console.error("Error fetching on-leave:", error);
    }
  };

  useEffect(() => {
    if (user.role === "HR" || user.role === "Executive" || user.role === "Employee") {
      fetchTimers();
      fetchOnLeave();
      const intervalId = setInterval(() => {
        fetchTimers();
        fetchOnLeave();
      }, 6000);
      return () => clearInterval(intervalId);
    }
  }, [user.role]);

  const fetchManagerTeams = async () => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/managers-with-employees");
      if (!response.ok) throw new Error("Failed to fetch managers-with-employees");
      const data = await response.json();
      // Dedupe by managerEmail - a manager promoted twice yields duplicate
      // ManagerLeads docs, which would otherwise render as repeated buttons.
      const seen = new Set();
      const uniqueManagers = (Array.isArray(data) ? data : []).filter((m) => {
        if (!m.managerEmail || seen.has(m.managerEmail)) return false;
        seen.add(m.managerEmail);
        return true;
      });
      setManagerTeams(uniqueManagers);
    } catch (error) {
      console.error("Error fetching manager teams:", error);
    }
  };

  useEffect(() => {
    if (user.role === "HR" || user.role === "Executive") {
      fetchManagerTeams();
    }
  }, [user.role]);

  // Manager's own team roster (Employee role) - so "Actives Now" can show the
  // full team status (active + inactive + on-leave), not just active people.
  const fetchManagerRoster = async () => {
    try {
      const res = await fetch(
        `https://hr-backend-72v1.onrender.com/employees-under-manager?email=${user.email}`
      );
      if (!res.ok) throw new Error("Failed to fetch manager roster");
      const data = await res.json();
      setManagerRoster(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching manager roster:", error);
    }
  };

  useEffect(() => {
    if (user.role === "Employee") fetchManagerRoster();
  }, [user.role]);

  const fetchTotalEmployees = async () => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/totalEmployees");
      const data = await response.json();
      setTotalEmployees(data.totalEmployees);
    } catch (error) {
      console.error("Error fetching total employees:", error);
    }
  };

  const fetchTotalManagerEmployees = async () => {
    try {
      const response = await fetch(
        "https://hr-backend-72v1.onrender.com/managerEmployeesCount",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ managerEmail: user.email }),
        }
      );
      const data = await response.json();
      setTotalEmployees(data.employees.length);
    } catch (error) {
      console.error("Error fetching total employees:", error);
    }
  };

  useEffect(() => {
    if (user.role === "HR" || user.role === "Executive") {
      fetchTotalEmployees();
    } else if (user.role === "Employee") {
      fetchTotalManagerEmployees();
    }
  }, [user.role]);

  const fetchPendingHrLeaves = async () => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/pending-hr-leaves-count");
      if (!response.ok) throw new Error("Failed to fetch pending HR leaves");
      const data = await response.json();
      setPendingHrLeavesCount(data.count);
    } catch (error) {
      console.error("Error fetching pending HR leaves:", error);
    }
  };

  useEffect(() => {
    if (user.role === "HR" || user.role === "Executive") {
      fetchPendingHrLeaves();
    }
  }, [user.role]);

  const fetchNewHiresStats = async () => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/new-hires-stats");
      if (!response.ok) throw new Error("Failed to fetch new hires stats");
      const data = await response.json();
      setNewHiresCount(data.currentCount);
      setNewHiresBadge(data.badge);
      setNewHiresList(data.hires || []);
    } catch (error) {
      console.error("Error fetching new hires stats:", error);
    }
  };

  useEffect(() => {
    if (user.role === "HR" || user.role === "Executive") {
      fetchNewHiresStats();
    }
  }, [user.role]);

  const fetchWeeklyAttendance = async () => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/weekly-attendance");
      if (!response.ok) throw new Error("Failed to fetch weekly attendance");
      const data = await response.json();
      setWeeklyAttendance(data.days || []);
      setWeeklyAvgPct(data.avgPct || 0);
    } catch (error) {
      console.error("Error fetching weekly attendance:", error);
    }
  };

  useEffect(() => {
    if (user.role === "HR" || user.role === "Executive") {
      fetchWeeklyAttendance();
    }
  }, [user.role]);

  // Recent Employees
  const [recentEmployees, setRecentEmployees] = useState([]);

  const fetchRecentEmployees = async () => {
    try {
      const res = await fetch("https://hr-backend-72v1.onrender.com/recent-employees");
      if (res.ok) setRecentEmployees(await res.json());
    } catch (e) {
      console.error("Error fetching recent employees:", e);
    }
  };

  useEffect(() => {
    if (user.role === "HR" || user.role === "Executive") {
      fetchRecentEmployees();
    }
  }, [user.role]);

  useEffect(() => {
    if (user.role === "HR" || user.role === "Executive") {
      fetch("https://hr-backend-72v1.onrender.com/showleaves")
        .then(r => r.json())
        .then(data => {
          const now = new Date();
          const pktOffset = 5 * 60 * 60 * 1000;
          const pktNow = new Date(now.getTime() + pktOffset);
          const pktYesterdayStart = new Date(pktNow);
          pktYesterdayStart.setUTCDate(pktYesterdayStart.getUTCDate() - 1);
          pktYesterdayStart.setUTCHours(0, 0, 0, 0);

          const filtered = data
            .filter(l =>
              l.managerStatus === "Accepted" &&
              l.hrStatus === "Pending" &&
              new Date(l.toDate).getTime() >= pktYesterdayStart.getTime()
            )
            .sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));
          setPendingLeaves(filtered);
        })
        .catch(() => {});
    }
  }, [user.role]);

  const handleLeaveAction = (leave, status) => {
    fetch("https://hr-backend-72v1.onrender.com/update-leave-status", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        role: "hr",
        email: leave.email,
        status,
        leaveId: leave._id,
        fromDate: leave.fromDate,
        toDate: leave.toDate,
        leaveType: leave.leaveType,
      }),
    })
      .then(r => r.json())
      .then(() => {
        setPendingLeaves(prev => prev.filter(l => l._id !== leave._id));
        setLeaveToast({ visible: true, message: status === "Accepted" ? "Leave Approved" : "Leave Declined" });
        setTimeout(() => setLeaveToast({ visible: false, message: "" }), 3000);
      })
      .catch(() => {});
  };

  const activeByEmail = React.useMemo(() => {
    const map = new Map();
    activeEmployees.forEach((emp) => map.set(emp.email, emp));
    return map;
  }, [activeEmployees]);

  const selectedManager = React.useMemo(() => {
    return managerTeams.find((m) => m.managerEmail === selectedManagerEmail);
  }, [managerTeams, selectedManagerEmail]);

  const displayedRows = React.useMemo(() => {
    // Inactive row - attach approved-leave info (null unless on manager-approved leave)
    const asInactive = (name, email) => ({
      name,
      email,
      isActive: false,
      leaveApproval: onLeaveByEmail[email] || null,
    });
    let rows;
    if (user.role === "Employee") {
      // Manager's own team - full roster (active + inactive + on-leave)
      rows = managerRoster.map(({ name, email }) => {
        const active = activeByEmail.get(email);
        if (active) return { ...active, isActive: true };
        return asInactive(name || email?.split("@")[0] || "-", email);
      });
    } else if (!selectedManager || selectedManagerEmail === "ALL") {
      rows = activeEmployees.map((emp) => ({ ...emp, isActive: true }));
    } else {
      rows = selectedManager.employees.map((email) => {
        const active = activeByEmail.get(email);
        if (active) return { ...active, isActive: true };
        const inferredName = email?.split("@")[0] || "-";
        return asInactive(inferredName, email);
      });
    }
    return rows.sort((a, b) => {
      if (!a.checkIn && !b.checkIn) return 0;
      if (!a.checkIn) return 1;
      if (!b.checkIn) return -1;
      return new Date(b.checkIn) - new Date(a.checkIn);
    });
  }, [activeByEmail, activeEmployees, selectedManager, selectedManagerEmail, managerRoster, onLeaveByEmail, user.role]);

  const computedActiveCount = React.useMemo(() => {
    return displayedRows.filter((r) => r.isActive).length;
  }, [displayedRows]);

  const cardData = [
    {
      label: "Total Employees",
      count: totalEmployees,
      subtitle: "Active team members",
      icon: "people",
    },
    {
      label: "Active Employees",
      count: selectedManagerEmail === "ALL" ? activeEmployeesCount : computedActiveCount,
      subtitle: "Currently working",
      icon: "person-check",
    },
    ...(user.role === "HR" || user.role === "Executive"
      ? [
          {
            label: "Pending Leaves",
            count: pendingHrLeavesCount,
            subtitle: "Awaiting approval",
            icon: "calendar",
          },
          {
            label: "New Hires",
            count: newHiresCount,
            subtitle: "This month",
            icon: "person-add",
            badge: newHiresBadge,
          },
        ]
      : []),
  ];

  const ccDateStr = React.useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date());
  }, []);

  const ccAccentByLabel = React.useMemo(() => {
    const dark = {
      "Total Employees": "#D4AF37",
      "Active Employees": "#34D399",
      "Pending Leaves": "#FBBF24",
      "New Hires": "#60A5FA",
    };
    const light = {
      "Total Employees": "#C7A13A",
      "Active Employees": "#2DBA89",
      "Pending Leaves": "#E9B02A",
      "New Hires": "#4F8FEF",
    };
    return isDark ? dark : light;
  }, [isDark]);

  const hexToRgba = (hex, alpha) => {
    const clean = String(hex || "").replace("#", "");
    if (clean.length !== 6) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  return (
    <>
      {isCommandCenter && (
        <div className="ccHeader">
          <div className="ccTitle">Command Center</div>
          <div className="ccSubtitle">
            Welcome back, {user?.name} - {ccDateStr}
          </div>
        </div>
      )}

      <div className="dashboardFront">
        {cardData.map((item, index) => {
          const IconComponent = ICON_MAP[item.icon];
          const isNewHires = item.label === "New Hires";
          const isLast = index === cardData.length - 1;
          const accent = isCommandCenter ? (ccAccentByLabel[item.label] || (isDark ? "#D4AF37" : "#C7A13A")) : null;
          const accentTint = accent ? hexToRgba(accent, isDark ? 0.14 : 0.12) : null;
          return (
            <div
              className={`statCard ${isDark ? "statCard--dark" : "statCard--light"} ${isCommandCenter ? "statCard--cc" : ""}`}
              key={index}
              style={{ position: "relative" }}
              onMouseEnter={() => isNewHires && setShowNewHiresPopup(true)}
              onMouseLeave={() => isNewHires && setShowNewHiresPopup(false)}
            >
              {isCommandCenter && (
                <div
                  className="statTopAccent"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                  }}
                />
              )}
              <div className="statCardTop">
                <div className="statIconBox" style={isCommandCenter ? { backgroundColor: accentTint } : undefined}>
                  {IconComponent && <IconComponent className="statIcon" />}
                </div>
                {item.badge != null && (
                  <span
                    className="statBadge"
                    style={
                      item.badge === "New" || item.badge.startsWith("+")
                        ? { color: "#16a34a", backgroundColor: "rgba(34,197,94,0.13)" }
                        : { color: "#dc2626", backgroundColor: "rgba(220,38,38,0.12)" }
                    }
                  >
                    {item.badge}
                  </span>
                )}
              </div>
              <div className="statNumber" style={isCommandCenter ? { color: accent } : undefined}>
                {item.count}
              </div>
              <div className={`statLabel ${isCommandCenter ? "ccStatLabel" : ""}`}>{item.label}</div>
              <div className="statSubtitle">{item.subtitle}</div>

              {/* New Hires hover popup */}
              {isNewHires && showNewHiresPopup && (
                <div
                  className={`newHiresPopup ${isDark ? "newHiresPopup--dark" : "newHiresPopup--light"}`}
                  style={isLast ? { right: 0 } : { left: 0 }}
                >
                  <div className="newHiresPopupTitle">Hired This Month</div>
                  {newHiresList.length === 0 ? (
                    <div className="newHiresPopupEmpty">No hires this month</div>
                  ) : (
                    newHiresList.map((h, i) => {
                      const d = new Date(h.joining);
                      const label = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
                      return (
                        <div key={i} className="newHiresPopupRow">
                          <span className="newHiresPopupName">{h.name}</span>
                          <span className="newHiresPopupDate">{label}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(user.role === "HR" || user.role === "Executive") && (
        <>
        <div className="chartAndActionsRow">
          {/* Weekly Attendance Chart - 65% */}
          <div className={`weeklyChartCard ${isDark ? "weeklyChartCard--dark" : "weeklyChartCard--light"}`}>
            <div className="weeklyChartHeader">
              <div>
                <div className="weeklyChartTitle">Weekly Attendance</div>
                <div className="weeklyChartSubtitle">Employee attendance rate</div>
              </div>
              <span className="weeklyChartBadge">
                ↗ {weeklyAvgPct}% avg
              </span>
            </div>

            <div className="weeklyChartBody">
              {/* Y-axis */}
              <div className="weeklyYAxis">
                {[100, 75, 50, 25, 0].map(tick => (
                  <span key={tick} className="weeklyYLabel">{tick}%</span>
                ))}
              </div>
              <div className="weeklyChartRight">
                {/* Plot area - bars + gridlines */}
                <div className="weeklyPlotArea">
                  {[25, 50, 75].map(tick => (
                    <div key={tick} className="weeklyGridLine" style={{ bottom: `${tick}%` }} />
                  ))}
                  {weeklyAttendance.map((d, idx) => {
                    const isFirst = idx === 0;
                    const isLast  = idx === weeklyAttendance.length - 1;
                    const alignStyle = isFirst
                      ? { left: 0 }
                      : isLast
                      ? { right: 0, left: "auto" }
                      : { left: "50%", transform: "translateX(-50%)" };
                    const tipData = tooltipCache[d.date];
                    return (
                      <div
                        className="weeklyChartCol"
                        key={d.day}
                        onMouseEnter={() => handleBarHover(d.date)}
                        onMouseLeave={handleBarLeave}
                      >
                        <div className="weeklyBarPct">{d.percentage}%</div>
                        <div className="weeklyBar" style={{ height: `${Math.max(d.percentage, 2)}%` }} />

                        {hoveredBar === d.date && (
                          <div
                            className={`barTooltip ${isDark ? "barTooltip--dark" : "barTooltip--light"}`}
                            style={{ position: "absolute", bottom: "calc(100% + 10px)", width: 252, ...alignStyle }}
                            onMouseEnter={handleTooltipEnter}
                            onMouseLeave={handleBarLeave}
                          >
                            {!tipData ? (
                              <div className="barTooltipInner barTooltipLoading">Loading…</div>
                            ) : (
                              <div className="barTooltipInner">
                                <div className="barTooltipDate">{d.day}</div>

                                {/* Checked In */}
                                <div className="barTooltipSection">
                                  <div className="barTooltipSectionHead">
                                    <span className="barTooltipDot barTooltipDot--green" />
                                    <span className="barTooltipSectionTitle">Checked In ({tipData.checkedIn.length})</span>
                                  </div>
                                  {tipData.checkedIn.length === 0
                                    ? <div className="barTooltipEmpty">None</div>
                                    : tipData.checkedIn.map((emp) => (
                                        <div key={emp.email} className="barTooltipRow">
                                          <span className="barTooltipName">{emp.name}</span>
                                          <span className="barTooltipTime">({emp.time})</span>
                                        </div>
                                      ))
                                  }
                                </div>

                                {/* Absent */}
                                <div className="barTooltipSection">
                                  <div className="barTooltipSectionHead">
                                    <span className="barTooltipDot barTooltipDot--red" />
                                    <span className="barTooltipSectionTitle">Absent ({tipData.absent.length})</span>
                                  </div>
                                  {tipData.absent.length === 0
                                    ? <div className="barTooltipEmpty">None</div>
                                    : tipData.absent.map((emp) => (
                                        <div key={emp.email} className="barTooltipRow">
                                          <span className="barTooltipName">{emp.name}</span>
                                        </div>
                                      ))
                                  }
                                </div>

                                {/* On Leave */}
                                <div className="barTooltipSection">
                                  <div className="barTooltipSectionHead">
                                    <span className="barTooltipDot barTooltipDot--orange" />
                                    <span className="barTooltipSectionTitle">On Leave ({tipData.onLeave.length})</span>
                                  </div>
                                  {tipData.onLeave.length === 0
                                    ? <div className="barTooltipEmpty">None</div>
                                    : tipData.onLeave.map((emp) => (
                                        <div key={emp.email} className="barTooltipRow">
                                          <span className="barTooltipName">{emp.name}</span>
                                        </div>
                                      ))
                                  }
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Day labels row */}
                <div className="weeklyDayLabels">
                  {weeklyAttendance.map((d) => (
                    <div className="weeklyBarDay" key={d.day}>
                      <span className="weeklyDayName">{d.day.split(" ")[0]}</span>
                      <span className="weeklyDateStr">{d.day.split(" ")[1]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="weeklyChartLegend">
              <span className="legendDot legendDot--orange" />
              <span className="legendLabel">Present</span>
              {/* <span className="legendDot legendDot--gray" /> */}
              {/* <span className="legendLabel">Absent</span> */}
            </div>
          </div>

          {/* Quick Actions - 35% */}
          <div className={`quickActionsCard ${isDark ? "quickActionsCard--dark" : "quickActionsCard--light"}`}>
            <div className="quickActionsHeader">
              <div className="quickActionsTitle">Quick Actions</div>
              <div className="quickActionsSubtitle">Common HR operations</div>
            </div>
            <div className="quickActionsGrid">
              <button className={`quickActionBtn ${isDark ? "quickActionBtn--dark" : "quickActionBtn--light"}`} onClick={() => setShowAddEmpModal(true)}>
                <BsPersonFillAdd className="quickActionIcon" />
                <span className="quickActionLabel">Add Employee</span>
                <span className="quickActionSub">Onboard new team member</span>
              </button>
              <button className={`quickActionBtn ${isDark ? "quickActionBtn--dark" : "quickActionBtn--light"}`} onClick={() => setShowScheduleLeaveModal(true)}>
                <BsCalendar3 className="quickActionIcon" />
                <span className="quickActionLabel">Schedule Leave</span>
                <span className="quickActionSub">Request time off</span>
              </button>
              <button className={`quickActionBtn ${isDark ? "quickActionBtn--dark" : "quickActionBtn--light"}`} onClick={() => setShowReportModal(true)}>
                <BsFileEarmarkBarGraph className="quickActionIcon" />
                <span className="quickActionLabel">Generate Report</span>
                <span className="quickActionSub">Export HR analytics</span>
              </button>
              <button className={`quickActionBtn ${isDark ? "quickActionBtn--dark" : "quickActionBtn--light"}`} onClick={() => setShowBirthdayModal(true)}>
                <BsGift className="quickActionIcon" />
                <span className="quickActionLabel">Birthdays</span>
                <span className="quickActionSub">Upcoming celebrations</span>
              </button>
            </div>
          </div>
        </div>{/* end chartAndActionsRow */}

        {/* Recent Employees + Leave Requests row */}
        <div className="recentAndLeaveRow">
          {/* Recent Employees card - 65% */}
          <div className={`recentEmpCard ${isDark ? "recentEmpCard--dark" : "recentEmpCard--light"}`}>
            {/* Card header */}
            <div className="recentEmpHeader">
              <div>
                <div className="recentEmpTitle">Recent Employees</div>
                <div className="recentEmpSubtitle">Latest team members overview</div>
              </div>
              <button
                className={`recentEmpViewAll ${isDark ? "recentEmpViewAll--dark" : "recentEmpViewAll--light"}`}
                onClick={() => onViewAllEmployees && onViewAllEmployees()}
              >
                View All
              </button>
            </div>

            {recentEmployees.length === 0 ? (
              <div className="recentEmpEmpty">No new employees in the last 30 days</div>
            ) : (
              <div className="recentEmpTable" style={{ tableLayout: "fixed", width: "100%" }}>
                {/* Table head */}
                <div className={`recentEmpThead ${isDark ? "recentEmpThead--dark" : "recentEmpThead--light"}`}>
                  <div className="recentEmpCol recentEmpCol--emp" style={{ width: "34%" }}>Employee</div>
                  <div className="recentEmpCol recentEmpCol--dept" style={{ width: "22%" }}>Department</div>
                  <div className="recentEmpCol recentEmpCol--joining" style={{ width: "22%" }}>Joining Date</div>
                  <div className="recentEmpCol recentEmpCol--status" style={{ width: "22%" }}>Status</div>
                </div>

                {/* Table body */}
                {recentEmployees.map((emp, i) => {
                  const statusStyle =
                    emp.status === "Active"
                      ? { backgroundColor: "rgba(34,197,94,0.15)", color: "#16a34a" }
                      : emp.status === "On Leave"
                      ? { backgroundColor: "rgba(212,175,55,0.15)", color: "#D4AF37" }
                      : isDark
                      ? { backgroundColor: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.45)" }
                      : { backgroundColor: "rgba(0,0,0,0.06)", color: "#6b7280" };

                  return (
                    <div
                      key={emp.email}
                      className={`recentEmpRow ${isDark ? "recentEmpRow--dark" : "recentEmpRow--light"} ${i % 2 === 1 ? (isDark ? "recentEmpRow--altDark" : "recentEmpRow--altLight") : ""}`}
                    >
                      {/* Employee cell */}
                      <div className="recentEmpCol recentEmpCol--emp" style={{ width: "34%", overflow: "hidden" }}>
                        {/* minWidth:0 lets this row shrink to the column. Without it the
                            default min-width:auto pins it at its content width, so it
                            overflows and the column's overflow:hidden clips the name
                            mid-word with no ellipsis. */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
                          <Avatar
                            src={emp.profilepic && emp.profilepic.trim() !== "" ? emp.profilepic : undefined}
                            sx={{
                              width: 38, height: 38, fontSize: 16, flexShrink: 0,
                              bgcolor: isDark ? "#D4AF37" : "#3b82f6",
                            }}
                          >
                            {(emp.name || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <div style={{ minWidth: 0, overflow: "hidden" }}>
                            <div
                              className={`recentEmpName ${isDark ? "recentEmpName--dark" : "recentEmpName--light"}`}
                              title={emp.name}
                              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {emp.name}
                            </div>
                            {emp.pos && (
                              <div
                                className={`recentEmpPos ${isDark ? "recentEmpPos--dark" : "recentEmpPos--light"}`}
                                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              >
                                {emp.pos}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Department */}
                      <div className={`recentEmpCol recentEmpCol--dept recentEmpCell ${isDark ? "recentEmpCell--dark" : "recentEmpCell--light"}`} style={{ width: "22%" }}>
                        {emp.department || "-"}
                      </div>

                      {/* Joining Date */}
                      <div className={`recentEmpCol recentEmpCol--joining recentEmpCell ${isDark ? "recentEmpCell--dark" : "recentEmpCell--light"}`} style={{ width: "22%" }}>
                        {emp.joining
                          ? new Date(emp.joining).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                          : "-"}
                      </div>

                      {/* Status badge */}
                      <div className="recentEmpCol recentEmpCol--status" style={{ width: "22%" }}>
                        <span className="recentEmpStatus" style={statusStyle}>
                          {emp.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Leave Requests card - 35% */}
          <div className={`leaveReqCard ${isDark ? "leaveReqCard--dark" : "leaveReqCard--light"}`}>
            {/* Header */}
            <div className="leaveReqHeader">
              <div className="leaveReqTitle">Leave Requests</div>
              {pendingLeaves.length > 0
                ? <span className="leaveReqBadge">{pendingLeaves.length} Pending</span>
                : <span className="leaveReqBadgeClear">All Clear</span>
              }
            </div>

            {/* Body */}
            <div className="leaveReqList">
              {pendingLeaves.length === 0
                ? <div className={`leaveReqEmpty ${isDark ? "leaveReqEmpty--dark" : "leaveReqEmpty--light"}`}>No pending leave requests ✓</div>
                : pendingLeaves.map(leave => {
                    const fmt = d => new Date(d).toLocaleDateString("en-US", { month: "short", day: "2-digit" });
                    const days = countWorkingDaysBetween(leave.fromDate, leave.toDate, holidaySet);
                    return (
                      <div key={leave._id} className={`leaveReqEntry ${isDark ? "leaveReqEntry--dark" : "leaveReqEntry--light"}`}>
                        <div className="leaveReqTop">
                          <Avatar sx={{ width: 38, height: 38, fontSize: 16, flexShrink: 0, bgcolor: isDark ? "#D4AF37" : "#3b82f6" }}>
                            {(leave.name || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <div className="leaveReqInfo">
                            <div className={`leaveReqName ${isDark ? "leaveReqName--dark" : "leaveReqName--light"}`}>{leave.name}</div>
                            <div className={`leaveReqType ${isDark ? "leaveReqType--dark" : "leaveReqType--light"}`}>{leave.leaveType}</div>
                          </div>
                          <div className="leaveReqDates">
                            <div className={`leaveReqRange ${isDark ? "leaveReqRange--dark" : "leaveReqRange--light"}`}>{fmt(leave.fromDate)} – {fmt(leave.toDate)}</div>
                            <div className={`leaveReqDays ${isDark ? "leaveReqDays--dark" : "leaveReqDays--light"}`}>{days} day{days !== 1 ? "s" : ""}</div>
                          </div>
                        </div>
                        <div className="leaveReqActions">
                          <button
                            className={`leaveReqBtn ${isDark ? "leaveReqBtn--approveDark" : "leaveReqBtn--approveLight"}`}
                            onClick={() => handleLeaveAction(leave, "Accepted")}
                          >
                            ✓ Approve
                          </button>
                          <button
                            className={`leaveReqBtn ${isDark ? "leaveReqBtn--declineDark" : "leaveReqBtn--declineLight"}`}
                            onClick={() => handleLeaveAction(leave, "Rejected")}
                          >
                            ✗ Decline
                          </button>
                        </div>
                      </div>
                    );
                  })
              }
            </div>
          </div>
        </div>
      </>
      )}

      {/* Add Employee Stepper Modal */}
      {showAddEmpModal && (
        ReactDOM.createPortal(
          <div className="addEmpModalOverlay" onClick={() => setShowAddEmpModal(false)}>
            <div className={`stepperModal ${isDark ? "stepperModal--dark" : "stepperModal--light"}`} onClick={(e) => e.stopPropagation()}>

              {/* Header */}
              <div className="stepperModalHeader">
                <h2 className="stepperModalTitle">Add Employee</h2>
                <button className="stepperCloseBtn" onClick={() => setShowAddEmpModal(false)}>
                  <IoClose />
                </button>
              </div>

              {addEmpSuccess ? (
                /* Success screen */
                <div className="stepperSuccess">
                  <div className="stepperSuccessIcon"><BsCheck2 /></div>
                  <h3>Employee Added Successfully!</h3>
                  <p>Credentials have been sent to {addEmpEmail}</p>
                  <button className="stepperNavBtn stepperNavBtn--next" onClick={() => setShowAddEmpModal(false)}>
                    Close
                  </button>
                </div>
              ) : (
                <>
                  {/* Stepper bar */}
                  <div className="stepperBar">
                    {["Basic Info", "Job Details", "Account & Bank"].map((label, i) => (
                      <React.Fragment key={i}>
                        <div className="stepperStep">
                          <div className={`stepperCircle ${addEmpStep > i ? "stepperCircle--done" : addEmpStep === i ? "stepperCircle--active" : "stepperCircle--inactive"}`}>
                            {addEmpStep > i ? <BsCheck2 /> : i + 1}
                          </div>
                          <span className={`stepperLabel ${addEmpStep === i ? "stepperLabel--active" : ""}`}>{label}</span>
                        </div>
                        {i < 2 && <div className={`stepperConnector ${addEmpStep > i ? "stepperConnector--done" : ""}`} />}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Scrollable step content */}
                  <div className="stepperContent">

                    {/* ── Step 1: Basic Info ── */}
                    {addEmpStep === 0 && (
                      <div>
                        <div className="stepSectionHeading">
                          <BsPersonFill className="stepSectionIcon" />
                          <span>Basic Information</span>
                        </div>
                        <div className="stepGrid">

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpName ? "stepInput--filled" : ""} ${addEmpErrors.name ? "stepInput--error" : ""}`} value={addEmpName} onChange={e => setAddEmpName(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Full Name *</label>
                          </div>
                          {addEmpErrors.name && <span className="stepFieldError">{addEmpErrors.name}</span>}
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpCnic ? "stepInput--filled" : ""}`} value={addEmpCnic} onChange={e => setAddEmpCnic(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">CNIC</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpPhone ? "stepInput--filled" : ""}`} value={addEmpPhone} onChange={e => setAddEmpPhone(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Phone</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpEmerPhone ? "stepInput--filled" : ""}`} value={addEmpEmerPhone} onChange={e => setAddEmpEmerPhone(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Emergency Phone</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpPermAddr ? "stepInput--filled" : ""}`} value={addEmpPermAddr} onChange={e => setAddEmpPermAddr(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Permanent Address</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpPresentAddr ? "stepInput--filled" : ""}`} value={addEmpPresentAddr} onChange={e => setAddEmpPresentAddr(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Present Address</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap stepFieldWrap--full">
                          <div className="stepFieldGroup">
                            <input type="date" className={`stepInput stepInput--date ${addEmpDob ? "stepInput--filled" : ""}`} value={addEmpDob} onChange={e => setAddEmpDob(e.target.value)} />
                            <label className="stepInputLabel stepInputLabel--alwaysShrunk">Date of Birth</label>
                          </div>
                        </div>

                        </div>
                      </div>
                    )}

                  {/* ── Step 2: Job Details ── */}
                  {addEmpStep === 1 && (
                    <div>
                      <div className="stepSectionHeading">
                        <BsBriefcaseFill className="stepSectionIcon" />
                        <span>Job Details</span>
                      </div>
                      <div className="stepGrid">

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <select className={`stepInput stepInput--select ${addEmpDept ? "stepInput--filled" : ""}`} value={addEmpDept} onChange={e => setAddEmpDept(e.target.value)}>
                              <option value=""></option>
                              {addEmpDepartments.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                            <label className={`stepInputLabel ${addEmpDept ? "stepInputLabel--alwaysShrunk" : ""}`}>Department</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <select className={`stepInput stepInput--select ${addEmpManager ? "stepInput--filled" : ""}`} value={addEmpManager ? JSON.stringify(addEmpManager) : ""} onChange={e => setAddEmpManager(e.target.value ? JSON.parse(e.target.value) : null)}>
                              <option value=""></option>
                              {addEmpManagers.map(m => (
                                <option key={m.email} value={JSON.stringify({ name: m.name, email: m.email })}>{m.name} - {m.email}</option>
                              ))}
                            </select>
                            <label className={`stepInputLabel ${addEmpManager ? "stepInputLabel--alwaysShrunk" : ""}`}>Manager</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpPos ? "stepInput--filled" : ""}`} value={addEmpPos} onChange={e => setAddEmpPos(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Position</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpSalary ? "stepInput--filled" : ""}`} value={addEmpSalary} onChange={e => setAddEmpSalary(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Salary</label>
                          </div>
                        </div>

                        <div className="stepFieldWrap stepFieldWrap--full">
                          <div className="stepFieldGroup">
                            <input type="date" className={`stepInput stepInput--date ${addEmpJoining ? "stepInput--filled" : ""}`} value={addEmpJoining} onChange={e => setAddEmpJoining(e.target.value)} />
                            <label className="stepInputLabel stepInputLabel--alwaysShrunk">Joining Date</label>
                          </div>
                        </div>

                      </div>
                    </div>
                  )}

                  {/* ── Step 3: Account & Bank ── */}
                  {addEmpStep === 2 && (
                    <div>
                      <div className="stepSectionHeading">
                        <BsLockFill className="stepSectionIcon" />
                        <span>Account & Bank</span>
                      </div>
                      <div className="stepGrid">

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup">
                            <input type="email" className={`stepInput ${addEmpEmail ? "stepInput--filled" : ""} ${addEmpErrors.email ? "stepInput--error" : ""}`} value={addEmpEmail} onChange={e => { setAddEmpEmail(e.target.value); setAddEmpDupEmail(false); }} placeholder=" " />
                            <label className="stepInputLabel">Email *</label>
                          </div>
                          {addEmpErrors.email && <span className="stepFieldError">{addEmpErrors.email}</span>}
                          {addEmpDupEmail && <span className="stepFieldError">An employee with this email already exists</span>}
                        </div>

                        <div className="stepFieldWrap">
                          <div className="stepFieldGroup stepFieldGroup--pass">
                            <input type={addEmpShowPass ? "text" : "password"} className={`stepInput stepInput--pass ${addEmpPass ? "stepInput--filled" : ""} ${addEmpErrors.pass ? "stepInput--error" : ""}`} value={addEmpPass} onChange={e => setAddEmpPass(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Login Password *</label>
                            <button type="button" className="passToggleBtn" onClick={() => setAddEmpShowPass(s => !s)}>
                              {addEmpShowPass ? <BsEyeSlash /> : <BsEye />}
                            </button>
                          </div>
                          {addEmpErrors.pass && <span className="stepFieldError">{addEmpErrors.pass}</span>}
                        </div>

                        <div className="stepFieldWrap stepFieldWrap--full">
                          <div className="stepFieldGroup">
                            <input className={`stepInput ${addEmpBankD ? "stepInput--filled" : ""}`} value={addEmpBankD} onChange={e => setAddEmpBankD(e.target.value)} placeholder=" " />
                            <label className="stepInputLabel">Bank Details</label>
                          </div>
                        </div>

                      </div>
                    </div>
                  )}

                  </div>{/* end stepperContent */}

                  {/* Footer navigation */}
                  <div className="stepperFooter">
                    {addEmpStep > 0 && (
                      <button className="stepperNavBtn stepperNavBtn--prev" onClick={() => setAddEmpStep(s => s - 1)}>
                        Previous
                      </button>
                    )}
                    <div style={{ flex: 1 }} />
                    {addEmpStep < 2 ? (
                      <button className="stepperNavBtn stepperNavBtn--next" onClick={handleAddEmpNext}>
                        Next
                      </button>
                    ) : (
                      <button className="stepperNavBtn stepperNavBtn--next" onClick={handleAddEmpSubmit} disabled={addEmpProcessing}>
                        {addEmpProcessing ? "Adding..." : "Add Employee"}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )
      )}

      {/* Schedule Leave Modal */}
      {showScheduleLeaveModal && (
        ReactDOM.createPortal(
          <div className="addEmpModalOverlay" onClick={() => setShowScheduleLeaveModal(false)}>
            <div
              className={`leaveModalContent ${isDark ? "leaveModalContent--dark" : "leaveModalContent--light"}`}
              onClick={(e) => e.stopPropagation()}
            >
            {/* Modal header */}
            <div className="leaveModalHeader">
              <div className="leaveModalTitle">Schedule Leave</div>
              <button className="addEmpModalClose leaveModalCloseBtn" onClick={() => setShowScheduleLeaveModal(false)}>
                <IoClose />
              </button>
            </div>

            {/* Toggle buttons */}
            <div className="leaveModalToggleRow">
              <button
                className="leaveToggleBtn"
                style={
                  leaveActiveType === "Annual Leave"
                    ? { backgroundColor: "#D4AF37", color: "#000", borderColor: "#D4AF37" }
                    : { backgroundColor: isDark ? "rgba(10,10,10,0.72)" : "transparent", color: "#D4AF37", borderColor: "#D4AF37" }
                }
                onClick={() => { setLeaveActiveType("Annual Leave"); setLeaveSuccess(false); setLeaveError(""); }}
              >
                Annual Leave
              </button>
              <button
                className="leaveToggleBtn"
                style={
                  leaveActiveType === "Personal Days Off"
                    ? { backgroundColor: "#D4AF37", color: "#000", borderColor: "#D4AF37" }
                    : { backgroundColor: isDark ? "rgba(10,10,10,0.72)" : "transparent", color: "#D4AF37", borderColor: "#D4AF37" }
                }
                onClick={() => { setLeaveActiveType("Personal Days Off"); setLeaveSuccess(false); setLeaveError(""); }}
              >
                Personal Days Off
              </button>
            </div>

            {/* Two-panel layout */}
            <div className="leaveModalPanels">
              {/* Left panel - stat cards */}
              <div className="leaveStatPanel">
                {[
                  {
                    label: `${leaveActiveType} Credited`,
                    count: leaveActiveType === "Annual Leave" ? creditedAnnualLeave : creditedPersonalDays,
                  },
                  {
                    label: `${leaveActiveType} Remaining`,
                    count: leaveActiveType === "Annual Leave" ? remainingAnnualLeave : remainingPersonalDays,
                  },
                ].map((item, i) => (
                  <div key={i} className={`leaveStatCard ${isDark ? "leaveStatCard--dark" : "leaveStatCard--light"}`}>
                    <div className="statCardTop">
                      <div className="statIconBox">
                        <BsBarChartLine className="statIcon" />
                      </div>
                    </div>
                    <div className="statNumber">{item.count}</div>
                    <div className="statLabel">{item.label}</div>
                    <div className="statSubtitle">Days</div>
                  </div>
                ))}
              </div>

              {/* Right panel - form */}
              <div className="leaveFormPanel">
                <h4 className={`leaveFormTitle ${isDark ? "leaveFormTitle--dark" : "leaveFormTitle--light"}`}>
                  Request Leave
                </h4>
                <div className="leaveFormFields">
                  <div style={{ display: "flex", gap: "16px" }}>
                    <div style={{ flex: 1 }}>
                      <label className={`leaveInputLabel ${isDark ? "leaveInputLabel--dark" : "leaveInputLabel--light"}`}>
                        From Date
                      </label>
                      <input
                        type="date"
                        className={`leaveInputField ${isDark ? "leaveInputField--dark" : "leaveInputField--light"}`}
                        value={leaveFromDate}
                        onChange={(e) => setLeaveFromDate(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className={`leaveInputLabel ${isDark ? "leaveInputLabel--dark" : "leaveInputLabel--light"}`}>
                        To Date
                      </label>
                      <input
                        type="date"
                        className={`leaveInputField ${isDark ? "leaveInputField--dark" : "leaveInputField--light"}`}
                        value={leaveToDate}
                        onChange={(e) => setLeaveToDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <label className={`leaveInputLabel ${isDark ? "leaveInputLabel--dark" : "leaveInputLabel--light"}`}>
                      Reason for Leave
                    </label>
                    <textarea
                      className={`leaveTextarea ${isDark ? "leaveTextarea--dark" : "leaveTextarea--light"}`}
                      rows={6}
                      placeholder="Enter your reason..."
                      value={leaveReason}
                      onChange={(e) => setLeaveReason(e.target.value)}
                    />
                  </div>
                  {leaveError && <p className="leaveErrorMsg">{leaveError}</p>}
                  {leaveSuccess && <p className="leaveSuccessMsg">✔ Leave request submitted successfully!</p>}
                  <button
                    className="leaveSubmitBtn"
                    onClick={handleLeaveSubmit}
                    disabled={leaveSubmitting}
                  >
                    {leaveSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>,
          document.body
        )
      )}

      {/* Birthdays Modal */}
      {showBirthdayModal && (
        ReactDOM.createPortal(
          <div className="addEmpModalOverlay" onClick={() => setShowBirthdayModal(false)}>
            <div
              className={`bdModal ${isDark ? "bdModal--dark" : "bdModal--light"}`}
              onClick={e => e.stopPropagation()}
            >
            {/* Header */}
            <div className="bdModalHeader">
              <div>
                <div className="bdModalTitle">Upcoming Birthdays 🎂</div>
                <div className="bdModalSub">Next 30 days</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {!birthdayLoading && (
                  <span className={`bdCountPill ${isDark ? "bdCountPill--dark" : "bdCountPill--light"}`}>
                    {birthdayData.length} Upcoming
                  </span>
                )}
                <button
                  className={`rptCloseBtn ${isDark ? "rptCloseBtn--dark" : "rptCloseBtn--light"}`}
                  onClick={() => setShowBirthdayModal(false)}
                >
                  <IoClose />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="bdContent">
              {birthdayLoading && (
                <div className="rptLoadingMsg">Loading birthdays…</div>
              )}
              {!birthdayLoading && birthdayData.length === 0 && (
                <div className="bdEmpty">No upcoming birthdays in the next 30 days 🎈</div>
              )}
              {!birthdayLoading && birthdayData.map((emp, i) => {
                const isToday = emp.daysUntil === 0;
                const isTomorrow = emp.daysUntil === 1;
                const bdDate = new Date(emp.birthday).toLocaleDateString("en-US", { month: "short", day: "numeric" });

                let daysLabel, daysClass;
                if (isToday)       { daysLabel = "Today! 🎉"; daysClass = "bdDays--today"; }
                else if (isTomorrow) { daysLabel = "Tomorrow!"; daysClass = "bdDays--tomorrow"; }
                else                { daysLabel = `in ${emp.daysUntil} days`; daysClass = "bdDays--soon"; }

                return (
                  <div
                    key={emp.email || i}
                    className={`bdCard ${isDark ? "bdCard--dark" : "bdCard--light"} ${isToday ? (isDark ? "bdCard--todayDark" : "bdCard--todayLight") : ""}`}
                  >
                    {/* Avatar */}
                    <Avatar
                      src={emp.profilepic && emp.profilepic.trim() !== "" ? emp.profilepic : undefined}
                      sx={{
                        width: 46, height: 46, fontSize: 18, flexShrink: 0,
                        bgcolor: isDark ? "#D4AF37" : "#3b82f6",
                      }}
                    >
                      {(emp.name || "?").charAt(0).toUpperCase()}
                    </Avatar>

                    {/* Name + designation */}
                    <div className="bdInfo">
                      <div className="bdName">{emp.name}</div>
                      {emp.pos && <div className="bdPos">{emp.pos}</div>}
                    </div>

                    {/* Date badge + days away */}
                    <div className="bdRight">
                      <div className={`bdDateBadge ${isDark ? "bdDateBadge--dark" : "bdDateBadge--light"}`}>
                        {bdDate}
                      </div>
                      <div className={`bdDays ${daysClass}`}>{daysLabel}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </div>,
          document.body
        )
      )}

      {/* Generate Report Modal */}
      {showReportModal && (
        ReactDOM.createPortal(
          <div className="addEmpModalOverlay" onClick={() => setShowReportModal(false)}>
            <div
              className={`rptModal ${isDark ? "rptModal--dark" : "rptModal--light"}`}
              onClick={e => e.stopPropagation()}
            >
            {/* Header */}
            <div className="rptModalHeader">
              <div className="rptModalTitle">Generate Report</div>
              <button className={`rptCloseBtn ${isDark ? "rptCloseBtn--dark" : "rptCloseBtn--light"}`} onClick={() => setShowReportModal(false)}>
                <IoClose />
              </button>
            </div>

            {/* Tab Bar */}
            <div className={`rptTabBar ${isDark ? "rptTabBar--dark" : "rptTabBar--light"}`}>
              {["Progress Reports", "Pending Items", "Export"].map((label, i) => (
                <button
                  key={i}
                  className={`rptTab ${isDark ? "rptTab--dark" : "rptTab--light"} ${reportModalTab === i ? (isDark ? "rptTab--activeDark" : "rptTab--activeLight") : ""}`}
                  onClick={() => setReportModalTab(i)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Scrollable Content */}
            <div className="rptContent">

              {/* ── Tab 1: Progress Reports ── */}
              {reportModalTab === 0 && (
                <div>
                  {rptLoading && <div className="rptLoadingMsg">Loading today's reports…</div>}
                  {!rptLoading && rptData && (
                    <>
                      {/* Submitted */}
                      <div className="rptSection">
                        <div className="rptSectionHead">
                          <span className="rptDot rptDot--green" />
                          <span className="rptSectionTitle">Submitted ({rptData.submitted.length})</span>
                        </div>
                        {rptData.submitted.length === 0
                          ? <div className="rptAllClear">All clear ✓ - no submissions yet</div>
                          : rptData.submitted.map(emp => (
                            <div key={emp.email} className={`rptEmpCard ${isDark ? "rptEmpCard--dark" : "rptEmpCard--light"}`}>
                              <div
                                className="rptEmpName"
                                onClick={() => setRptExpandedEmail(rptExpandedEmail === emp.email ? null : emp.email)}
                              >
                                <span>{emp.name}</span>
                                <span className="rptExpandIcon">{rptExpandedEmail === emp.email ? "▲" : "▼"}</span>
                              </div>
                              {rptExpandedEmail === emp.email && (
                                <div className="rptEmpReport">{emp.reportDetail}</div>
                              )}
                            </div>
                          ))
                        }
                      </div>

                      {/* Not Submitted */}
                      <div className="rptSection">
                        <div className="rptSectionHead">
                          <span className="rptDot rptDot--red" />
                          <span className="rptSectionTitle">Not Submitted ({rptData.notSubmitted.length})</span>
                        </div>
                        {rptData.notSubmitted.length === 0
                          ? <div className="rptAllClear">All clear ✓ - everyone submitted</div>
                          : rptData.notSubmitted.map(emp => (
                            <div key={emp.email} className={`rptEmpCard ${isDark ? "rptEmpCard--dark" : "rptEmpCard--light"}`}>
                              <div className="rptEmpName rptEmpName--plain">
                                <span>{emp.name}</span>
                                <span className="rptEmpSub">{emp.email}</span>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Tab 2: Pending Items ── */}
              {reportModalTab === 1 && (
                <div>
                  {pendingLoading && <div className="rptLoadingMsg">Loading pending items…</div>}
                  {!pendingLoading && pendingSummary && (
                    <>
                      {/* Pending Leave Requests */}
                      <div className="rptSection">
                        <div className="rptSectionHead">
                          <span className="rptDot rptDot--orange" />
                          <span className="rptSectionTitle">Pending Leave Requests ({pendingSummary.pendingLeaves.length})</span>
                        </div>
                        {pendingSummary.pendingLeaves.length === 0
                          ? <div className="rptAllClear">All clear ✓</div>
                          : pendingSummary.pendingLeaves.map((l, i) => (
                            <div key={i} className={`rptEmpCard ${isDark ? "rptEmpCard--dark" : "rptEmpCard--light"}`}>
                              <div className="rptEmpName rptEmpName--plain">
                                <span>{l.name}</span>
                                <span className="rptPendingBadge">{l.leaveType}</span>
                              </div>
                              <div className="rptPendingMeta">
                                {new Date(l.fromDate).toLocaleDateString()} - {new Date(l.toDate).toLocaleDateString()}
                              </div>
                              {l.reason && <div className="rptPendingReason">"{l.reason}"</div>}
                            </div>
                          ))
                        }
                      </div>

                      {/* Pending Employee Update Requests */}
                      <div className="rptSection">
                        <div className="rptSectionHead">
                          <span className="rptDot rptDot--blue" />
                          <span className="rptSectionTitle">Pending Update Requests ({pendingSummary.pendingUpdateRequests.length})</span>
                        </div>
                        {pendingSummary.pendingUpdateRequests.length === 0
                          ? <div className="rptAllClear">All clear ✓</div>
                          : pendingSummary.pendingUpdateRequests.map((r, i) => (
                            <div key={i} className={`rptEmpCard ${isDark ? "rptEmpCard--dark" : "rptEmpCard--light"}`}>
                              <div className="rptEmpName rptEmpName--plain">
                                <span>{r.name}</span>
                                <span className="rptEmpSub">{r.email}</span>
                              </div>
                              <div className="rptPendingMeta">
                                Field: <strong>{r.field}</strong> → {String(r.value)}
                              </div>
                            </div>
                          ))
                        }
                      </div>

                    </>
                  )}
                </div>
              )}

              {/* ── Tab 3: Export ── */}
              {reportModalTab === 2 && (
                <div className="rptExportGrid">
                  {/* Export Attendance */}
                  <div className={`rptExportCard ${isDark ? "rptExportCard--dark" : "rptExportCard--light"}`}>
                    <div className="rptExportCardTitle">Export Attendance Report</div>
                    <div className="rptExportCardSub">Download attendance log + hours summary as Excel (.xlsx)</div>
                    <div className="rptDateRow">
                      <div className="rptDateField">
                        <label className={`rptDateLabel ${isDark ? "rptDateLabel--dark" : "rptDateLabel--light"}`}>From</label>
                        <input
                          type="date"
                          className={`rptDateInput ${isDark ? "rptDateInput--dark" : "rptDateInput--light"}`}
                          value={expAttFrom}
                          onChange={e => setExpAttFrom(e.target.value)}
                        />
                      </div>
                      <div className="rptDateField">
                        <label className={`rptDateLabel ${isDark ? "rptDateLabel--dark" : "rptDateLabel--light"}`}>To</label>
                        <input
                          type="date"
                          className={`rptDateInput ${isDark ? "rptDateInput--dark" : "rptDateInput--light"}`}
                          value={expAttTo}
                          onChange={e => setExpAttTo(e.target.value)}
                        />
                      </div>
                    </div>
                    <button
                      className={`rptExportBtn ${isDark ? "rptExportBtn--dark" : "rptExportBtn--light"}`}
                      onClick={handleExportAttendance}
                      disabled={expAttLoading || !expAttFrom || !expAttTo}
                    >
                      {expAttLoading ? "Exporting…" : "Download CSV"}
                    </button>
                    <div className="rptExportCols">Sheet 1 - Attendance Log: Employee Name, Email, Date, Check-in Time, Check-out Time &nbsp;|&nbsp; Sheet 2 - Employee Hours Summary: Employee Name, Email, Total Hours Worked</div>
                  </div>

                  {/* Export Progress Reports */}
                  <div className={`rptExportCard ${isDark ? "rptExportCard--dark" : "rptExportCard--light"}`}>
                    <div className="rptExportCardTitle">Export Progress Reports</div>
                    <div className="rptExportCardSub">Download employee progress reports as CSV</div>
                    <div className="rptDateRow">
                      <div className="rptDateField">
                        <label className={`rptDateLabel ${isDark ? "rptDateLabel--dark" : "rptDateLabel--light"}`}>From</label>
                        <input
                          type="date"
                          className={`rptDateInput ${isDark ? "rptDateInput--dark" : "rptDateInput--light"}`}
                          value={expRptFrom}
                          onChange={e => setExpRptFrom(e.target.value)}
                        />
                      </div>
                      <div className="rptDateField">
                        <label className={`rptDateLabel ${isDark ? "rptDateLabel--dark" : "rptDateLabel--light"}`}>To</label>
                        <input
                          type="date"
                          className={`rptDateInput ${isDark ? "rptDateInput--dark" : "rptDateInput--light"}`}
                          value={expRptTo}
                          onChange={e => setExpRptTo(e.target.value)}
                        />
                      </div>
                    </div>
                    <button
                      className={`rptExportBtn ${isDark ? "rptExportBtn--dark" : "rptExportBtn--light"}`}
                      onClick={handleExportReports}
                      disabled={expRptLoading || !expRptFrom || !expRptTo}
                    >
                      {expRptLoading ? "Exporting…" : "Download CSV"}
                    </button>
                    <div className="rptExportCols">Columns: Employee Name, Email, Date, Report</div>
                  </div>
                </div>
              )}

            </div>{/* end rptContent */}
            </div>
          </div>,
          document.body
        )
      )}

      <div className="dashboardBottom">
        {(user.role === "HR" || user.role === "Executive") && managerTeams.length > 0 && (
          <div style={{ marginBottom: 12 }} className="managerFilterGroup">
            <Button
              className="managerFilterBtn"
              onClick={() => setSelectedManagerEmail("ALL")}
              style={{
                backgroundColor: selectedManagerEmail === "ALL" ? filterColor : "transparent",
                borderColor: filterColor,
                color: selectedManagerEmail === "ALL" ? "#FFFFFF" : filterColor,
              }}
            >
              All
            </Button>
            {managerTeams
              .filter((m) => (m.managerName || "").toLowerCase() !== "testt")
              .map((mgr) => {
                const firstName = (mgr.managerName || "").split(" ")[0] || mgr.managerEmail;
                const isActive = selectedManagerEmail === mgr.managerEmail;
                return (
                  <Button
                    className="managerFilterBtn"
                    key={mgr.managerEmail}
                    onClick={() => setSelectedManagerEmail(mgr.managerEmail)}
                    style={{
                      backgroundColor: isActive ? filterColor : "transparent",
                      borderColor: filterColor,
                      color: isActive ? "#FFFFFF" : filterColor,
                    }}
                  >
                    {firstName}
                  </Button>
                );
              })}
          </div>
        )}
        <TableContainer
          component={Paper}
          className="dashboardTimerTableContainer"
          variant="outlined"
        >
          <h4 className="myTableHeader">EMPLOYEE STATUS</h4>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow className="dashboardTableRow">
                <TableCell className="myTableHeadCells">
                  <span className="myTableHeadSpan">Name</span>
                </TableCell>
                <TableCell className="myTableHeadCells" align="left">
                  <span className="myTableHeadSpan">Email</span>
                </TableCell>
                <TableCell className="myTableHeadCells" align="left">
                  <span className="myTableHeadSpan">Online</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows.map((emp) => (
                <TableRow className="dashboardTableRow" key={emp.email}>
                  <TableCell sx={{ fontSize: "large" }}>{emp.name || "-"}</TableCell>
                  <TableCell sx={{ fontSize: "large" }} align="left">
                    {emp.email}
                  </TableCell>
                  <TableCell sx={{ fontSize: "large" }} align="left">
                    {emp.isActive ? (
                      new Intl.DateTimeFormat("en-US", {
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true,
                        timeZone: "UTC",
                      }).format(new Date(emp.checkIn))
                    ) : emp.leaveApproval ? (
                      <span className="empStatusLeave">
                        ON LEAVE
                        <span className="empStatusLeaveInfo">
                          <BsInfoCircle />
                          <span className="empStatusTip">
                            {emp.leaveApproval === "both"
                              ? "Approved by Manager & HR"
                              : "Approved by Manager"}
                          </span>
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: "#d32f2f", fontWeight: 600 }}>INACTIVE</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {leaveToast.visible && (
        <div className={`leaveToast ${isDark ? "leaveToast--dark" : "leaveToast--light"}`}>
          {leaveToast.message}
        </div>
      )}
    </>
  );
};

export default Dashboard;
